

















import { Component, Vue, Watch } from "vue-property-decorator";
import { Location } from "vue-router";
import AdminCockpitCardComponent, {
  AdminCockpitCardData,
  AdminCockpitFigure,
} from "@/components/admin/cockpit/AdminCockpitCard.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { ClientAdministrationRow } from "@/data/models/ClientAdministrationRow";
import { ExternalSystemRow } from "@/data/models/ClientPartner";
import { toQueryParams } from "@/data/models/DeviceReportFilters";
import {
  cachedClientsForAdministration,
  cachedExternalSystems,
  listClientsForAdministration,
  listExternalSystems,
} from "@/data/repository/ClientAdministrationRepository";
import { loadDeviceReportRows } from "@/data/repository/DeviceReportLoader";
import { cachedInfoConfigVersions, listInfoConfigVersions } from "@/data/repository/InfoConfigAdministrationRepository";
import { listMiraExceptionSuggestions } from "@/data/repository/MiraExceptionAdministrationRepository";
import { cachedUnresolvedInsurers, listUnresolvedInsurers } from "@/data/repository/MiraInsurerAdministrationRepository";
// getReactiveActiveUser, not getActiveUser: the gate feeds the template, and getActiveUser() reads
// localStorage, so a role change in-session would not re-render until a page refresh.
import { getReactiveActiveUser } from "@/data/repository/UserRepository";
import { manageClientsAccess, manageProductionAccess } from "@/permissions/UserPermissions";
import {
  buildClientFigureCounts,
  CLIENT_CONTRACT_FIGURE_FILTERS,
  CLIENT_STATUS_FIGURE_FILTERS,
  CLIENT_TOTAL_FIGURES,
  clientListQueryFor,
  clientListQueryForTotal,
  EXTERNAL_SYSTEM_TASK_STATUSES,
  externalSystemCount,
  infoConfigTargetFigures,
  InfoConfigTargetFigures,
  liveCustomerTotal,
  waitingDeviceReportFilters,
} from "@/utils/AdminCockpitFigures";
import { EMPTY_VALUE } from "@/utils/ClientAdministrationDetail";
import { ClientListFilter } from "@/utils/ClientAdministrationList";
import { figuresUpdatedAtText } from "@/utils/ClientListFigures";
import {
  COLOR_COCKPIT_EXTRAS,
  COLOR_COCKPIT_EXTRAS_DARK,
  COLOR_COCKPIT_MANAGEMENT,
  COLOR_COCKPIT_MANAGEMENT_DARK,
  COLOR_COCKPIT_MY_TASKS,
  COLOR_COCKPIT_MY_TASKS_DARK,
} from "@/utils/colors";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";

/** Each server call the cockpit makes, loaded and failing on its own. */
type CockpitSource = "clients" | "externalSystems" | "insurers" | "suggestions" | "infoConfig" | "waitingReports";

type SourceState = "idle" | "loading" | "loaded" | "failed";

/** The sources behind `MANAGE_CLIENTS`, the Mandanten list's capability. */
const CLIENT_SOURCES: CockpitSource[] = ["clients", "externalSystems", "insurers", "suggestions", "infoConfig"];

/** The sources behind `MANAGE_PRODUCTION`, the Testberichte capability. */
const PRODUCTION_SOURCES: CockpitSource[] = ["waitingReports"];

/**
 * The cockpit of the IRES Admin (portal) client, shown instead of the normal cockpit.
 *
 * The normal cockpit is built from the logged-in company's own projects and devices, which means
 * nothing for the portal client. The router sends the portal client here wherever it would reach
 * `/main/cockpit` (see `getCockpitPath`), so the normal cockpit itself is untouched.
 *
 * It is the support staff's to-do screen (Taimoor, 2026-10-28): the Aufgaben card first, then the
 * clients' figures. Each figure is the count of the screen it opens, computed with that screen's own
 * rule, so a figure and the rows behind it always agree. Cards and items are capability-gated one by
 * one, like the portal landing page.
 *
 * **Not the access boundary.** The route guard limits this screen to portal clients and each callable
 * repeats its own checks.
 */
@Component({
  // Explicit, so Scaffold's keep-alive still matches it once the build minifies the class name.
  name: "AdminCockpitComponent",
  components: {
    "admin-cockpit-card": AdminCockpitCardComponent,
    "content-state": ContentStateComponent,
  },
})
export default class AdminCockpitComponent extends Vue {
  clients: ClientAdministrationRow[] = [];
  /** When IRES Cloud last recounted the clients' figures, epoch ms. */
  figuresUpdatedAt: number | null = null;
  externalSystems: ExternalSystemRow[] = [];
  unresolvedInsurerCount = 0;
  exceptionSuggestionCount = 0;
  infoConfigTargets: InfoConfigTargetFigures | null = null;
  waitingReportCount = 0;
  /**
   * The sources being read right now, also while their figures already show (a cached answer, a quiet
   * refresh), so a read is never started twice. Nothing renders from it.
   */
  readsInFlight: Partial<Record<CockpitSource, boolean>> = {};
  /** Every key present from the start, so Vue tracks each one. */
  sourceStates: Record<CockpitSource, SourceState> = {
    clients: "idle",
    externalSystems: "idle",
    insurers: "idle",
    suggestions: "idle",
    infoConfig: "idle",
    waitingReports: "idle",
  };

  /** Same capability the Mandanten list's route guard enforces; the callables check it again. */
  get mayManageClients(): boolean {
    return manageClientsAccess(getReactiveActiveUser());
  }

  /** Same capability the Testberichte route guard enforces. */
  get mayManageProduction(): boolean {
    return manageProductionAccess(getReactiveActiveUser());
  }

  get cards(): AdminCockpitCardData[] {
    // Read the reactive inputs before branching, so the computed tracks them all from its first run.
    const mayManageClients = this.mayManageClients;
    const mayManageProduction = this.mayManageProduction;
    const clients = this.clients;
    const tasks = this.taskCard(mayManageClients, mayManageProduction);
    const cards: AdminCockpitCardData[] = tasks.figures.length > 0 ? [tasks] : [];

    if (!mayManageClients) {
      return cards;
    }

    const clientsFailed = this.sourceStates.clients === "failed";

    return [
      ...cards,
      {
        key: "clients",
        title: this.$t("admin.clients.title").toString(),
        color: themed(COLOR_COCKPIT_MANAGEMENT, COLOR_COCKPIT_MANAGEMENT_DARK),
        figures: this.filterFigures(clients, CLIENT_STATUS_FIGURE_FILTERS),
        failed: clientsFailed,
      },
      {
        key: "contracts",
        title: this.$t("admin.cockpit.contractsAndApp").toString(),
        color: themed(COLOR_COCKPIT_EXTRAS, COLOR_COCKPIT_EXTRAS_DARK),
        figures: this.filterFigures(clients, CLIENT_CONTRACT_FIGURE_FILTERS),
        failed: clientsFailed,
      },
      this.totalsCard(clients),
      this.infoConfigCard(),
    ];
  }

  /** A count as the card shows it: "–" until its source has loaded, never a 0 it does not know. */
  shownCount(source: CockpitSource, count: number | null): string {
    return this.sourceStates[source] === "loaded" && count !== null ? formatNumber(count) : EMPTY_VALUE;
  }

  /** A task figure known to be 0: nothing waiting, so the card shows it greyed. */
  isDone(source: CockpitSource, count: number): boolean {
    return this.sourceStates[source] === "loaded" && count === 0;
  }

  failedAny(sources: CockpitSource[]): boolean {
    return sources.some((source) => this.sourceStates[source] === "failed");
  }

  filterFigures(clients: ClientAdministrationRow[], filters: ClientListFilter[]): AdminCockpitFigure[] {
    return buildClientFigureCounts(clients, filters).map(({ filter, count }) => ({
      id: filter,
      label: this.$t(`admin.clients.filters.${filter}`).toString(),
      value: this.shownCount("clients", count),
      location: { name: ComponentName.CLIENT_ADMINISTRATION_LIST, query: clientListQueryFor(filter) },
    }));
  }

  /**
   * The Aufgaben card: what is waiting for someone, each figure opening the screen where it is done.
   * Figures appear by capability, so a production-only user sees the Testberichte alone.
   */
  taskCard(mayManageClients: boolean, mayManageProduction: boolean): AdminCockpitCardData {
    const figures: AdminCockpitFigure[] = [];
    const sources: CockpitSource[] = [];

    if (mayManageClients) {
      sources.push("insurers", "suggestions", "externalSystems");
      figures.push(
        {
          id: "insurers",
          label: this.$t("admin.sections.miraInsurerReview.title").toString(),
          value: this.shownCount("insurers", this.unresolvedInsurerCount),
          location: { name: ComponentName.MIRA_INSURER_REVIEW },
          quiet: this.isDone("insurers", this.unresolvedInsurerCount),
        },
        {
          id: "suggestions",
          label: this.$t("admin.cockpit.exceptionSuggestions").toString(),
          value: this.shownCount("suggestions", this.exceptionSuggestionCount),
          location: { name: ComponentName.MIRA_EXCEPTION_LIST },
          quiet: this.isDone("suggestions", this.exceptionSuggestionCount),
        },
        ...EXTERNAL_SYSTEM_TASK_STATUSES.map((status) => {
          const count = externalSystemCount(this.externalSystems, status);

          return {
            id: `externalSystems_${status}`,
            label: this.$t("admin.cockpit.externalSystemsInStatus", [
              this.$t(`admin.clients.partners.statuses.${status}`),
            ]).toString(),
            value: this.shownCount("externalSystems", count),
            location: { name: ComponentName.EXTERNAL_SYSTEM_LIST, query: { filter: status } },
            quiet: this.isDone("externalSystems", count),
          };
        })
      );
    }

    if (mayManageProduction) {
      sources.push("waitingReports");
      figures.push({
        id: "waitingReports",
        label: this.$t("admin.cockpit.waitingDeviceReports").toString(),
        value: this.shownCount("waitingReports", this.waitingReportCount),
        location: { name: ComponentName.DEVICE_REPORT_RESULTS, query: toQueryParams(waitingDeviceReportFilters()) },
        quiet: this.isDone("waitingReports", this.waitingReportCount),
      });
    }

    return {
      key: "tasks",
      title: this.$t("admin.cockpit.tasks").toString(),
      color: themed(COLOR_COCKPIT_MY_TASKS, COLOR_COCKPIT_MY_TASKS_DARK),
      figures,
      failed: this.failedAny(sources),
    };
  }

  /** The Gesamt card: the live customers' figures added up, each opening those clients sorted by it. */
  totalsCard(clients: ClientAdministrationRow[]): AdminCockpitCardData {
    const updatedAt = figuresUpdatedAtText(this.figuresUpdatedAt, new Date());
    const scope = this.$t("admin.cockpit.totalsScope").toString();

    return {
      key: "totals",
      title: this.$t("admin.cockpit.totalsTitle").toString(),
      footnote: updatedAt ? `${scope} · ${this.$t("admin.clients.figuresUpdatedAt", [updatedAt])}` : scope,
      color: themed(COLOR_COCKPIT_EXTRAS, COLOR_COCKPIT_EXTRAS_DARK),
      figures: CLIENT_TOTAL_FIGURES.map(({ field, labelKey }) => ({
        id: field,
        label: this.$t(labelKey).toString(),
        value: this.shownCount("clients", liveCustomerTotal(clients, field)),
        location: { name: ComponentName.CLIENT_ADMINISTRATION_LIST, query: clientListQueryForTotal(field) },
      })),
      failed: this.failedAny(["clients"]),
    };
  }

  /** The Beschreibungen card: the version the fleet follows and who sets their own, all opening Ziele. */
  infoConfigCard(): AdminCockpitCardData {
    const targets = this.infoConfigTargets;
    const loaded = this.sourceStates.infoConfig === "loaded" && targets !== null;
    const location: Location = { name: ComponentName.INFO_CONFIG_LIST, query: { filter: "TARGETS" } };

    return {
      key: "infoConfig",
      title: this.$t("admin.sections.infoConfig.title").toString(),
      color: themed(COLOR_COCKPIT_EXTRAS, COLOR_COCKPIT_EXTRAS_DARK),
      figures: [
        {
          id: "globalTarget",
          label: this.$t("admin.infoConfig.badges.globalTarget").toString(),
          value: loaded && targets?.globalTarget ? targets.globalTarget : EMPTY_VALUE,
          location,
        },
        {
          id: "clientTargets",
          label: this.$t("admin.cockpit.ownTargets").toString(),
          value: this.shownCount("infoConfig", targets?.clientTargets ?? null),
          location,
        },
        {
          id: "testTargets",
          label: this.$t("admin.cockpit.testTargets").toString(),
          value: this.shownCount("infoConfig", targets?.testTargets ?? null),
          location,
        },
      ],
      failed: this.failedAny(["infoConfig"]),
    };
  }

  /** Loads the sources once their capability is known to be held; never calls a callable without it. */
  @Watch("mayManageClients", { immediate: true })
  onMayManageClientsChanged(mayManageClients: boolean) {
    if (mayManageClients) {
      CLIENT_SOURCES.forEach((source) => this.loadSource(source, false));
    }
  }

  @Watch("mayManageProduction", { immediate: true })
  onMayManageProductionChanged(mayManageProduction: boolean) {
    if (mayManageProduction) {
      PRODUCTION_SOURCES.forEach((source) => this.loadSource(source, false));
    }
  }

  /**
   * Loads one source. A quiet load (back from the cache) keeps the figures shown meanwhile and, should
   * it fail, keeps them rather than turning them into "–". A source never loaded is loaded normally.
   */
  async loadSource(source: CockpitSource, quiet: boolean) {
    const state = this.sourceStates[source];

    if (state === "loading" || this.readsInFlight[source]) {
      return;
    }

    // Never loaded, but read before in this tab (`AdminReadCache.ts`): its figures show at once, and
    // the read below is as quiet as a refresh.
    if (state === "idle" && this.showCachedSource(source)) {
      this.sourceStates[source] = "loaded";
    }

    const isQuiet = this.sourceStates[source] === "loaded" || (quiet && state !== "idle");
    if (!isQuiet) {
      this.sourceStates[source] = "loading";
    }

    this.readsInFlight[source] = true;
    try {
      await this.fetchSource(source);
      this.sourceStates[source] = "loaded";
    } catch {
      // Swallowed on purpose: the card says it is incomplete, and its other figures stay usable.
      if (!isQuiet) {
        this.sourceStates[source] = "failed";
      }
    } finally {
      this.readsInFlight[source] = false;
    }
  }

  /** Puts a source's cached answer on the cards; false when there is none (or the source is not cached). */
  showCachedSource(source: CockpitSource): boolean {
    switch (source) {
      case "clients": {
        const list = cachedClientsForAdministration();
        if (list) {
          this.clients = list.clients;
          this.figuresUpdatedAt = list.figuresUpdatedAt;
        }
        return list !== null;
      }
      case "externalSystems": {
        const systems = cachedExternalSystems();
        if (systems) {
          this.externalSystems = systems;
        }
        return systems !== null;
      }
      case "insurers": {
        const unresolved = cachedUnresolvedInsurers();
        if (unresolved) {
          this.unresolvedInsurerCount = unresolved.groups.length;
        }
        return unresolved !== null;
      }
      case "infoConfig": {
        const versions = cachedInfoConfigVersions();
        if (versions) {
          this.infoConfigTargets = infoConfigTargetFigures(versions);
        }
        return versions !== null;
      }
      default:
        return false;
    }
  }

  async fetchSource(source: CockpitSource) {
    switch (source) {
      case "clients": {
        const list = await listClientsForAdministration();
        this.clients = list.clients;
        this.figuresUpdatedAt = list.figuresUpdatedAt;
        break;
      }
      case "externalSystems":
        this.externalSystems = await listExternalSystems();
        break;
      case "insurers":
        this.unresolvedInsurerCount = (await listUnresolvedInsurers()).groups.length;
        break;
      case "suggestions":
        this.exceptionSuggestionCount = (await listMiraExceptionSuggestions()).length;
        break;
      case "infoConfig":
        this.infoConfigTargets = infoConfigTargetFigures(await listInfoConfigVersions());
        break;
      case "waitingReports":
        this.waitingReportCount = (await loadDeviceReportRows(waitingDeviceReportFilters())).rows.length;
        break;
    }
  }

  /**
   * Back from the cache: the figures are shown at once and refreshed quietly, since a client, system
   * or suggestion may have changed meanwhile. On the first visit every source is still loading, so
   * nothing is loaded twice.
   */
  activated() {
    if (this.mayManageClients) {
      CLIENT_SOURCES.forEach((source) => this.loadSource(source, true));
    }
    if (this.mayManageProduction) {
      PRODUCTION_SOURCES.forEach((source) => this.loadSource(source, true));
    }
  }
}
